/**
 * ContactHeroSvgs.tsx
 * Decorative Islamic SVG elements for the Contact hero section.
 * All animations use CSS keyframes (not Framer Motion) so they
 * survive SSR / page-reload reliably.
 */

/* ── Animated 8-pointed Islamic star ────────────────────────────────────
   Rotation is driven by the `spin-slow` CSS keyframe defined in
   animations.css. Using CSS avoids the transform-origin quirk that
   causes framer-motion's `motion.g` rotate to stall on hard refresh.     */
export function IslamicStarBg() {
  return (
    <div
      className="pointer-events-none absolute right-0 top-0 h-full w-1/2 opacity-[0.04] overflow-hidden"
      aria-hidden="true"
    >
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          animation: 'spin-slow 80s linear infinite',
          transformOrigin: '50% 50%',
        }}
        viewBox="0 0 600 600"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* 8-pointed star — two overlapping rotated squares */}
        <rect
          x="150"
          y="150"
          width="300"
          height="300"
          rx="8"
          fill="#FFB606"
          opacity="0.8"
        />
        <rect
          x="150"
          y="150"
          width="300"
          height="300"
          rx="8"
          fill="#FFB606"
          opacity="0.8"
          transform="rotate(45 300 300)"
        />

        {/* Inner ring squares */}
        <rect
          x="185"
          y="185"
          width="230"
          height="230"
          rx="4"
          fill="none"
          stroke="#FFB606"
          strokeWidth="2"
          opacity="0.5"
        />
        <rect
          x="185"
          y="185"
          width="230"
          height="230"
          rx="4"
          fill="none"
          stroke="#FFB606"
          strokeWidth="2"
          opacity="0.5"
          transform="rotate(45 300 300)"
        />

        {/* Centre circles */}
        <circle cx="300" cy="300" r="60" fill="#FFB606" opacity="0.6" />
        <circle
          cx="300"
          cy="300"
          r="80"
          fill="none"
          stroke="#FFB606"
          strokeWidth="1.5"
          opacity="0.4"
        />

        {/* Outer ring of 16 dots */}
        {Array.from({ length: 16 }).map((_, i) => {
          const angle = (i / 16) * 2 * Math.PI
          const r = 260
          const cx = 300 + r * Math.cos(angle)
          const cy = 300 + r * Math.sin(angle)
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r="4"
              fill="#FFB606"
              opacity="0.5"
            />
          )
        })}
      </svg>
    </div>
  )
}

/* ── Floating crescent + star ────────────────────────────────────────────
   Uses the `float-y` CSS keyframe for a smooth up-down drift.            */
export function CrescentDecor() {
  return (
    <svg
      className="pointer-events-none absolute right-12 top-1/2 -translate-y-1/2 h-48 w-48 opacity-15 max-lg:hidden animate-float-y"
      viewBox="0 0 160 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ animationDuration: '5s' }}
    >
      {/* Crescent */}
      <circle cx="80" cy="80" r="60" fill="#FFB606" />
      <circle cx="104" cy="68" r="50" fill="#1a1612" />
      {/* Star */}
      <path
        d="M120 38 L122.5 45.5 L130 45.5 L124 50.5 L126.5 58 L120 53 L113.5 58 L116 50.5 L110 45.5 L117.5 45.5 Z"
        fill="#FFB606"
      />
    </svg>
  )
}

/* ── Arabesque geometric ring — bottom-left corner ───────────────────── */
export function ArabesqueCorner() {
  return (
    <svg
      className="pointer-events-none absolute -bottom-8 -left-8 h-72 w-72 opacity-15 animate-spin-slow"
      viewBox="0 0 300 300"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg, i) => (
        <g key={i} transform={`rotate(${deg} 150 150)`}>
          <rect
            x="100"
            y="100"
            width="100"
            height="100"
            rx="4"
            fill="none"
            stroke="#FFB606"
            strokeWidth="1"
          />
        </g>
      ))}

      <circle
        cx="150"
        cy="150"
        r="70"
        stroke="#FFB606"
        strokeWidth="1"
        fill="none"
      />

      <circle
        cx="150"
        cy="150"
        r="45"
        stroke="#FFB606"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  )
}
